import React, { useEffect, useRef } from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, Typography } from '@mui/material';

import Button from 'components/Button';
import { WrapperProps } from 'components/Button/styles';
import { useThemeContext } from 'global/ThemeContext/ThemeContext';
import GlobalModal from 'modals/GlobalModal/GlobalModal';

export interface RegistrationPromptModalProps {
  isOpen: boolean;
  onDoItNow: () => void;
  onRemindLater: () => void;
  completionPercentage: number;
}

/**
 * Registration Prompt Modal Component
 * Displays when user has incomplete event registration form
 * Provides options to complete now or be reminded later
 */
const RegistrationPromptModal: React.FC<RegistrationPromptModalProps> = ({
  isOpen,
  onDoItNow,
  onRemindLater,
  completionPercentage,
}) => {
  const theme = useThemeContext();
  const primaryButtonRef = useRef<HTMLButtonElement>(null);

  /**
   * Focus trap and keyboard navigation
   * Focus primary button when modal opens
   */
  useEffect(() => {
    if (isOpen && primaryButtonRef.current) {
      // Small delay to ensure modal is fully rendered
      setTimeout(() => {
        primaryButtonRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  /**
   * Handle ESC key to trigger "Remind me later"
   */
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        onRemindLater();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onRemindLater]);

  return (
    <GlobalModal
      open={isOpen}
      title="Complete Seu Cadastro"
      onClose={onRemindLater}
      closeOnBackdropClick={false}
      closeOnEscapeKeyDown={true}
      width="35rem"
      height="auto"
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap="1.5rem"
        padding="1rem 0"
        role="dialog"
        aria-labelledby="onboarding-modal-title"
        aria-describedby="onboarding-modal-description"
      >
        {/* Icon */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            color: theme.primary,
            fontSize: '3rem',
          }}
          aria-hidden="true"
        >
          <CheckCircleOutlineIcon fontSize="inherit" />
        </Box>

        {/* Message */}
        <Box textAlign="center" id="onboarding-modal-description">
          <Typography
            variant="body1"
            sx={{
              color: theme.text.primary,
              fontSize: '1.125rem',
              lineHeight: '1.6',
              marginBottom: '0.5rem',
            }}
          >
            Você ainda não finalizou o preenchimento do seu cadastro. Informações importantes para
            se conectar com os membros da comunidade estão faltando.
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.text.primary,
              fontSize: '1.125rem',
              lineHeight: '1.6',
              fontWeight: 600,
            }}
          >
            Vamos fazer isso agora?
          </Typography>
        </Box>

        {/* Progress indicator with bar */}
        {completionPercentage > 0 && completionPercentage < 100 && (
          <Box
            sx={{
              width: '100%',
              maxWidth: '400px',
              padding: '1rem',
              backgroundColor: theme.background.primary,
              borderRadius: '12px',
              border: `1px solid ${theme.background.secondary}`,
            }}
            role="status"
            aria-live="polite"
          >
            {/* Percentage display */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              marginBottom="0.75rem"
            >
              <Typography
                variant="body2"
                sx={{
                  color: theme.text.secondary,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                Progresso do cadastro
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: theme.primary,
                  fontSize: '1.25rem',
                  fontWeight: 700,
                }}
              >
                {Math.round(completionPercentage)}%
              </Typography>
            </Box>

            {/* Progress bar */}
            <Box
              sx={{
                width: '100%',
                height: '8px',
                backgroundColor: theme.background.secondary,
                borderRadius: '4px',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  width: `${completionPercentage}%`,
                  height: '100%',
                  backgroundColor: theme.primary,
                  borderRadius: '4px',
                  transition: 'width 0.3s ease-in-out',
                  boxShadow: `0 0 8px ${theme.primary}40`,
                }}
              />
            </Box>

            {/* Completion message */}
            <Typography
              variant="caption"
              sx={{
                color: theme.text.secondary,
                fontSize: '0.75rem',
                marginTop: '0.5rem',
                display: 'block',
              }}
            >
              Faltam apenas {Math.round(100 - completionPercentage)}% para completar
            </Typography>
          </Box>
        )}

        {/* Action buttons */}
        <Box display="flex" gap="1rem" width="100%" justifyContent="center" marginTop="0.5rem">
          <Button
            variant="outline"
            onClick={onRemindLater}
            style={{ minWidth: '12rem' }}
            aria-label="Lembrar mais tarde, fechar modal"
            tabIndex={0}
          >
            Lembrar mais tarde
          </Button>
          <Button
            variant="primary"
            onClick={onDoItNow}
            style={{ minWidth: '12rem' }}
            ref={primaryButtonRef as unknown as React.RefObject<WrapperProps>}
            aria-label="Preencher formulário agora"
            tabIndex={0}
          >
            Fazer agora
          </Button>
        </Box>

        {/* Screen reader announcements */}
        <div
          className="sr-only"
          role="status"
          aria-live="assertive"
          aria-atomic="true"
          style={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          {isOpen && (
            <>
              Modal de cadastro aberto.
              {completionPercentage > 0 &&
                ` Seu cadastro está ${Math.round(completionPercentage)}% completo.`}
              Pressione Tab para navegar entre os botões. Pressione Escape para lembrar mais tarde.
            </>
          )}
        </div>
      </Box>
    </GlobalModal>
  );
};

export default RegistrationPromptModal;
